import { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useLanguage } from '../../hooks/useLanguage';
import { card, cardBody, btnPrimary, REG_FONTS, flagBar } from './styles';
import { leadService } from '../../services/leadService';
import { trackPixelAndCapi } from '../../lib/landing/pili-track';
import { useUTMParams } from '../../lib/landing/utm-params';
import { useMetaParams } from '../../lib/landing/meta-params';
import { buildAttributionPayload } from '../../lib/landing/attribution';
import { PhoneField } from '@/components/landing/PhoneField';
import { isValidLandingPhone } from '@/lib/landing/phone';
import type { Via, StoredAnswer } from './questions';

interface LeadContactFormProps {
  via: Via;
  eligible: boolean;
  answers: Record<string, StoredAnswer>;
}

export function LeadContactForm({ via, eligible, answers }: LeadContactFormProps) {
  const { t } = useTranslation('regularizacion');
  const { currentLang } = useLanguage();
  const utmParams = useUTMParams();
  const metaParams = useMetaParams();

  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [rgpd, setRgpd] = useState(false);
  const [adult, setAdult] = useState(false);
  const [status, setStatus] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle');

  const canSubmit =
    name.trim() && isValidLandingPhone(phone) && rgpd && adult && status === 'idle';

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;

    setStatus('sending');
    try {
      const fullPhone = phone.trim();
      await leadService.createContact({
        name: name.trim(),
        phone: fullPhone,
        via,
        eligible,
        lang: currentLang,
        answers: { ...answers, mayor_edad: 'yes' },
        // Procedencia de campaña Meta (antes este path no la reenviaba y todo
        // lead del quiz quedaba como 'organic' en el CRM).
        ...buildAttributionPayload(utmParams, metaParams),
      });
      trackPixelAndCapi('Lead', { name: name.trim(), phone: fullPhone }, {
        content_name: 'regularizacion_evaluator',
        content_category: via,
        eligible,
      }).catch(() => {});
      setStatus('sent');
    } catch {
      setStatus('error');
    }
  };

  const viaLabel = via === 'dt5' ? 'DT5' : 'DT6';
  const whatsappMsg = encodeURIComponent(t('contact.whatsappMsg', { via: viaLabel }));
  const whatsappUrl = `https://wa.me/34923720708?text=${whatsappMsg}`;

  if (status === 'sent') {
    return (
      <div className={card}>
        {flagBar}
        <div className={`${cardBody} text-center`}>
          <div className="text-4xl mb-3">&#10004;</div>
          <p
            className="text-lg font-bold text-[#2e7d32] mb-2"
            style={{ fontFamily: REG_FONTS.heading }}
          >
            {t('contact.sent')}
          </p>
          <p className="text-[#444] mb-5" style={{ fontFamily: REG_FONTS.body }}>
            {t('contact.sentDesc')}
          </p>
          <a
            href={whatsappUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-block bg-[#25D366] hover:bg-[#1da851] text-white font-semibold py-3 px-8 rounded-xl transition-colors duration-200"
            style={{ fontFamily: REG_FONTS.body }}
          >
            {t('contact.whatsapp')}
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className={card}>
      {flagBar}
      <div className={cardBody}>
        <h2
          className="text-xl font-bold text-[#1a1a1a] mb-2"
          style={{ fontFamily: REG_FONTS.heading }}
        >
          {t('contact.title')}
        </h2>
        <p className="text-[#444] mb-5" style={{ fontFamily: REG_FONTS.body }}>
          {t('contact.description')}
        </p>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Name */}
          <div>
            <label className="block text-sm font-semibold text-[#1a1a1a] mb-1" style={{ fontFamily: REG_FONTS.body }}>
              {t('contact.name')}
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t('contact.namePlaceholder')}
              className="w-full border-2 border-[#e0e0e0] rounded-xl py-3 px-4 text-[#1a1a1a] focus:border-[#2e7d32] focus:outline-none transition-colors"
              style={{ fontFamily: REG_FONTS.body }}
            />
          </div>

          {/* Phone */}
          <div>
            <label className="block text-sm font-semibold text-[#1a1a1a] mb-1" style={{ fontFamily: REG_FONTS.body }}>
              {t('contact.phone')}
            </label>
            <PhoneField
              value={phone}
              onChange={setPhone}
              placeholder={t('contact.phonePlaceholder')}
            />
          </div>

          {/* Mayor de edad */}
          <label className="flex items-start gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={adult}
              onChange={(e) => setAdult(e.target.checked)}
              className="mt-1 accent-[#2e7d32] w-4 h-4 cursor-pointer"
            />
            <span className="text-sm text-[#444]" style={{ fontFamily: REG_FONTS.body }}>
              {t('contact.adult')}
            </span>
          </label>

          {/* RGPD */}
          <label className="flex items-start gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={rgpd}
              onChange={(e) => setRgpd(e.target.checked)}
              className="mt-1 accent-[#2e7d32] w-4 h-4 cursor-pointer"
            />
            <span className="text-sm text-[#444]" style={{ fontFamily: REG_FONTS.body }}>
              <Trans
                i18nKey="contact.rgpd"
                t={t}
                components={{
                  1: (
                    <a
                      href="/privacidad"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-[#2e7d32] underline hover:text-[#1b5e20]"
                      onClick={(e) => e.stopPropagation()}
                    />
                  ),
                }}
              />
            </span>
          </label>

          {/* Submit */}
          <button
            type="submit"
            disabled={!canSubmit}
            className={`${btnPrimary} w-full`}
            style={{ fontFamily: REG_FONTS.body }}
          >
            {status === 'sending' ? t('contact.sending') : t('contact.submit')}
          </button>

          {status === 'error' && (
            <div className="text-center space-y-3" role="alert">
              <p className="text-sm text-[#c62828]" style={{ fontFamily: REG_FONTS.body }}>
                {t('contact.error')}
              </p>
              <a
                href={whatsappUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block bg-[#25D366] hover:bg-[#1da851] text-white font-semibold py-2 px-6 rounded-lg transition-colors duration-200"
                style={{ fontFamily: REG_FONTS.body }}
              >
                {t('contact.errorWhatsapp')}
              </a>
            </div>
          )}
        </form>
      </div>
    </div>
  );
}
